import type { Layout, LayoutItem } from "react-grid-layout";
import { getFirstCollision } from "react-grid-layout";

import type { WidgetKind } from "../widgetKind";
import type { WidgetPlacement } from "./workspace.types";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const RGL_COLS = 24;

/** Product width for in-grid order tickets on evaluation desk templates. */
export const ORDER_TICKET_FIXED_WIDTH_PX = 420;

/** Default and minimum width for watchlists on evaluation desk templates. */
export const WATCHLIST_MIN_WIDTH_PX = ORDER_TICKET_FIXED_WIDTH_PX;

/**
 * Authored column span for 420px desk widgets (ticket + watchlist). Runtime
 * pixel conversion replaces this when the viewport is wider or narrower.
 */
export const DESK_STANDARD_WIDTH_COLS = 7;

export const ORDER_TICKET_VERTICAL_RESIZE_HANDLES = ["n", "s"] as const;

export interface GridLayoutMetrics {
  containerWidth: number;
  cols: number;
  marginX: number;
  containerPaddingX: number;
}

/** Mirrors react-grid-layout's `calcGridColWidth`. */
export const calcGridColWidthPx = ({
  containerWidth,
  cols,
  marginX,
  containerPaddingX,
}: GridLayoutMetrics): number =>
  (containerWidth - marginX * (cols - 1) - containerPaddingX * 2) / cols;

/** Mirrors react-grid-layout's `calcGridItemWHPx` for width. */
export const pixelWidthForGridUnits = (
  w: number,
  colWidth: number,
  marginX: number,
): number => Math.round(colWidth * w + Math.max(0, w - 1) * marginX);

/** Mirrors react-grid-layout's `calcWHRaw` width half. */
export const gridUnitsForWidthPx = (
  targetWidthPx: number,
  colWidth: number,
  marginX: number,
): number =>
  Math.max(1, Math.round((targetWidthPx + marginX) / (colWidth + marginX)));

export const applyFixedWidthPlacements = (
  placements: readonly WidgetPlacement[],
  metrics: GridLayoutMetrics,
): WidgetPlacement[] => {
  if (metrics.containerWidth <= 0) {
    return [...placements];
  }

  const colWidth = calcGridColWidthPx(metrics);

  const sized = placements.map((placement) => {
    if (placement.fixedWidthPx !== undefined) {
      // Never exceed the grid: a 420px target on a ~420px board can round
      // above `cols` and collapse end-aligned packing.
      const w = Math.min(
        metrics.cols,
        gridUnitsForWidthPx(placement.fixedWidthPx, colWidth, metrics.marginX),
      );

      return {
        ...placement,
        w,
        minW: w,
        maxW: w,
        ...(placement.alignX === "end"
          ? { x: Math.max(0, metrics.cols - w) }
          : {}),
      };
    }

    if (placement.minWidthPx === undefined) {
      return placement;
    }

    const minW = Math.min(
      metrics.cols,
      gridUnitsForWidthPx(placement.minWidthPx, colWidth, metrics.marginX),
    );
    const isAuthoredDefault =
      placement.w === DESK_STANDARD_WIDTH_COLS || placement.w < minW;

    return {
      ...placement,
      minW,
      w: isAuthoredDefault ? minW : Math.max(placement.w, minW),
    };
  });

  return reserveRightEdgeForAlignedTickets(packDeskWatchlists(sized));
};

/** Pins 420px watchlists and lets flexible neighbors fill the leftover space. */
const packDeskWatchlists = (
  placements: WidgetPlacement[],
): WidgetPlacement[] => {
  const overlapsY = (a: WidgetPlacement, b: WidgetPlacement): boolean =>
    a.y < b.y + b.h && b.y < a.y + a.h;

  const tickets = placements.filter((placement) => placement.alignX === "end");
  const watchlists = placements.filter(
    (placement) => placement.minWidthPx !== undefined,
  );
  if (watchlists.length === 0) {
    return placements;
  }

  let next = placements;

  const patch = (widgetId: string, values: Partial<WidgetPlacement>) => {
    next = next.map((placement) =>
      placement.widgetId === widgetId ? { ...placement, ...values } : placement,
    );
  };

  for (const original of watchlists) {
    const watchlist = next.find(
      (placement) => placement.widgetId === original.widgetId,
    );
    if (!watchlist) {
      continue;
    }

    const ticket = tickets.find((candidate) => overlapsY(candidate, watchlist));
    if (!ticket) {
      continue;
    }

    const others = next.filter(
      (placement) =>
        placement.widgetId !== watchlist.widgetId &&
        placement.widgetId !== ticket.widgetId &&
        overlapsY(placement, ticket),
    );
    const leftmostOtherX = others.reduce(
      (min, placement) => Math.min(min, placement.x),
      Number.POSITIVE_INFINITY,
    );
    const isLeftmost = others.length === 0 || watchlist.x <= leftmostOtherX;

    if (isLeftmost) {
      patch(watchlist.widgetId, { x: 0, w: watchlist.w });
      const filler = [...others].sort((a, b) => a.x - b.x)[0];
      if (filler) {
        patch(filler.widgetId, {
          x: watchlist.w,
          w: Math.max(1, ticket.x - watchlist.w),
        });
      }
      continue;
    }

    const x = Math.max(0, ticket.x - watchlist.w);
    patch(watchlist.widgetId, { x, w: watchlist.w });
    const filler = [...others].sort((a, b) => a.x - b.x)[0];
    if (filler) {
      patch(filler.widgetId, { x: 0, w: Math.max(1, x) });
    }
  }

  return next;
};

/** Keeps end-aligned tickets flush to the right of their own band, with no gap. */
const reserveRightEdgeForAlignedTickets = (
  placements: WidgetPlacement[],
): WidgetPlacement[] => {
  const tickets = placements.filter((placement) => placement.alignX === "end");
  if (tickets.length === 0) {
    return placements;
  }

  const overlapsY = (a: WidgetPlacement, b: WidgetPlacement): boolean =>
    a.y < b.y + b.h && b.y < a.y + a.h;

  let next = placements;

  for (const ticket of tickets) {
    const reservedX = ticket.x;
    // Full-width ticket: nothing left to reserve; shrinking neighbors to
    // w=0 is worse than leaving their clamped spans alone.
    if (reservedX <= 0) {
      continue;
    }

    next = next.map((placement) => {
      if (
        placement.widgetId === ticket.widgetId ||
        placement.alignX === "end" ||
        !overlapsY(placement, ticket)
      ) {
        return placement;
      }
      if (placement.x >= reservedX) {
        const w = Math.max(1, Math.min(placement.w, reservedX));
        return { ...placement, x: Math.max(0, reservedX - w), w };
      }
      if (placement.x + placement.w > reservedX) {
        return { ...placement, w: Math.max(1, reservedX - placement.x) };
      }
      return placement;
    });

    const expandById = new Map<string, number>();
    const neighborsByRow = new Map<number, WidgetPlacement[]>();
    for (const placement of next) {
      if (
        placement.widgetId === ticket.widgetId ||
        placement.alignX === "end" ||
        !overlapsY(placement, ticket) ||
        placement.x >= reservedX ||
        placement.minWidthPx !== undefined
      ) {
        continue;
      }
      const row = neighborsByRow.get(placement.y) ?? [];
      row.push(placement);
      neighborsByRow.set(placement.y, row);
    }

    for (const row of neighborsByRow.values()) {
      const rightmost = row.reduce((winner, candidate) =>
        candidate.x + candidate.w > winner.x + winner.w ? candidate : winner,
      );
      const fillTo = next
        .filter(
          (placement) =>
            placement.widgetId !== rightmost.widgetId &&
            placement.widgetId !== ticket.widgetId &&
            overlapsY(placement, ticket) &&
            placement.x >= rightmost.x + rightmost.w,
        )
        .reduce((min, placement) => Math.min(min, placement.x), reservedX);
      const filledW = fillTo - rightmost.x;
      if (filledW > rightmost.w) {
        expandById.set(rightmost.widgetId, filledW);
      }
    }

    if (expandById.size === 0) {
      continue;
    }

    next = next.map((placement) => {
      const filledW = expandById.get(placement.widgetId);
      return filledW === undefined ? placement : { ...placement, w: filledW };
    });
  }

  return next;
};

// ---------------------------------------------------------------------------
// Per-kind defaults
// ---------------------------------------------------------------------------

/**
 * Default footprint and minimums keyed by widget kind. Canonical source for
 * RGL packing.
 */
export const WIDGET_PLACEMENT_DEFAULTS: Record<
  WidgetKind,
  Omit<WidgetPlacement, "widgetId" | "x" | "y">
> = {
  chart: { w: 12, h: 12, minW: 1, minH: 4 },
  portfolioHistory: { w: 12, h: 10, minW: 4, minH: 6 },
  balances: { w: 12, h: 10, minW: 3, minH: 6 },
  watchlist: { w: 6, h: 10, minW: 2, minH: 6 },
  optionsChain: { w: 12, h: 14, minW: 4, minH: 8 },
  positions: { w: 12, h: 10, minW: 1, minH: 6 },
  orders: { w: 12, h: 10, minW: 3, minH: 6 },
  activities: { w: 12, h: 10, minW: 3, minH: 6 },
  orderTicket: { w: 8, h: 14, minW: 3, minH: 8 },
};

/** Re-applies current kind minimums so saved layouts pick up default changes. */
export const applyKindMinSizes = (
  placements: readonly WidgetPlacement[],
  kindByWidgetId: Readonly<Record<string, WidgetKind>>,
): WidgetPlacement[] =>
  placements.map((placement) => {
    const kind = kindByWidgetId[placement.widgetId];
    if (!kind) {
      return placement;
    }
    const defaults = WIDGET_PLACEMENT_DEFAULTS[kind];
    const minW = defaults.minW ?? placement.w;
    const minH = defaults.minH ?? placement.h;
    return {
      ...placement,
      minW,
      minH,
      w: Math.max(placement.w, minW),
      h: Math.max(placement.h, minH),
    };
  });

// ---------------------------------------------------------------------------
// RGL ↔ engine-neutral adapters
// ---------------------------------------------------------------------------

/**
 * Converts an RGL `Layout` array to engine-neutral `WidgetPlacement[]`.
 * Absent x/y values normalise to 0. Returns a new sorted array; the input
 * is never mutated. No RGL library fields (callbacks, DOM refs) are retained.
 */
export const fromRglLayout = (layout: Layout): WidgetPlacement[] =>
  layout
    .map(
      (item): WidgetPlacement => ({
        widgetId: item.i,
        x: item.x ?? 0,
        y: item.y ?? 0,
        w: item.w,
        h: item.h,
        ...(item.minW !== undefined ? { minW: item.minW } : {}),
        ...(item.minH !== undefined ? { minH: item.minH } : {}),
        ...(item.maxW !== undefined ? { maxW: item.maxW } : {}),
        ...(item.maxH !== undefined ? { maxH: item.maxH } : {}),
      }),
    )
    .sort((a, b) => a.widgetId.localeCompare(b.widgetId));

/**
 * Converts engine-neutral `WidgetPlacement[]` to an RGL `Layout` array.
 * Maps `widgetId` → `i`. Returns a new sorted array.
 */
export const toRglLayout = (placements: WidgetPlacement[]): Layout =>
  placements
    .map(
      (p): LayoutItem => ({
        i: p.widgetId,
        x: p.x,
        y: p.y,
        w: p.w,
        h: p.h,
        ...(p.minW !== undefined ? { minW: p.minW } : {}),
        ...(p.minH !== undefined ? { minH: p.minH } : {}),
        ...(p.maxW !== undefined ? { maxW: p.maxW } : {}),
        ...(p.maxH !== undefined ? { maxH: p.maxH } : {}),
        ...(p.fixedWidthPx !== undefined
          ? { resizeHandles: [...ORDER_TICKET_VERTICAL_RESIZE_HANDLES] }
          : {}),
      }),
    )
    .sort((a, b) => a.i.localeCompare(b.i));

// ---------------------------------------------------------------------------
// First-fit packing
// ---------------------------------------------------------------------------

/**
 * Scans left-to-right, top-to-bottom for the first slot that fits `w × h`
 * inside a `cols`-wide grid. Falls back to one row below the tallest item
 * when every scanned position is occupied.
 */
const findNextPackPosition = (
  placements: WidgetPlacement[],
  w: number,
  h: number,
  cols: number,
): { x: number; y: number } => {
  const layout: Layout = placements.map((p) => ({
    i: p.widgetId,
    x: p.x,
    y: p.y,
    w: p.w,
    h: p.h,
  }));

  const maxY =
    layout.reduce((bottom, item) => Math.max(bottom, item.y + item.h), 0) + 1;

  for (let y = 0; y <= maxY; y += 1) {
    for (let x = 0; x <= cols - w; x += 1) {
      const candidate: LayoutItem = { i: "__pack__", x, y, w, h };
      if (!getFirstCollision(layout, candidate)) {
        return { x, y };
      }
    }
  }

  return { x: 0, y: maxY };
};

/**
 * Computes a default `WidgetPlacement` for a new widget of the given `kind`,
 * packed into the first free slot in the existing layout.
 */
export const createDefaultPlacement = (
  widgetId: string,
  kind: WidgetKind,
  existingPlacements: WidgetPlacement[],
): WidgetPlacement => {
  const defaults = WIDGET_PLACEMENT_DEFAULTS[kind];
  const { x, y } = findNextPackPosition(
    existingPlacements,
    defaults.w,
    defaults.h,
    RGL_COLS,
  );
  return { widgetId, x, y, ...defaults };
};
